/**
 * Decoder/Encoder Operations
 * Production-ready implementation of all encoding/decoding operations
 */

import { decode, encode } from 'html-entities';
import { decodeJwt, decodeProtectedHeader } from 'jose';
import type {
  DecoderResult,
  DecoderOperationType,
  SmartDecodeDetection,
} from '../types';

/**
 * Execute a decoder operation on input text
 */
export async function executeOperation({
  type,
  input,
}: {
  type: DecoderOperationType;
  input: string;
}): Promise<DecoderResult> {
  try {
    switch (type) {
      case 'url-decode':
        return urlDecode(input);
      case 'url-encode':
        return urlEncode(input);
      case 'base64-decode':
        return base64Decode(input);
      case 'base64-encode':
        return base64Encode(input);
      case 'hex-decode':
        return hexDecode(input);
      case 'hex-encode':
        return hexEncode(input);
      case 'html-decode':
        return htmlDecode(input);
      case 'html-encode':
        return htmlEncode(input);
      case 'unicode-decode':
        return unicodeDecode(input);
      case 'unicode-encode':
        return unicodeEncode(input);
      case 'jwt-decode':
        return jwtDecode(input);
      case 'json-pretty':
        return jsonPretty(input);
      case 'json-minify':
        return jsonMinify(input);
      case 'hash-sha1':
        return hashByAlgorithm({ input, algorithm: 'SHA-1' });
      case 'hash-sha256':
        return hashByAlgorithm({ input, algorithm: 'SHA-256' });
      case 'hash-sha512':
        return hashByAlgorithm({ input, algorithm: 'SHA-512' });
      case 'smart-decode':
        return smartDecode(input);
      default:
        return {
          success: false,
          output: '',
          error: `Unknown operation: ${type}`,
        };
    }
  } catch (error) {
    return {
      success: false,
      output: '',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// URL Operations
// ============================================================================

function urlDecode(input: string): DecoderResult {
  try {
    const decoded = decodeURIComponent(input);
    return { success: true, output: decoded };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: 'Invalid URL-encoded string',
    };
  }
}

function urlEncode(input: string): DecoderResult {
  try {
    const encoded = encodeURIComponent(input);
    return { success: true, output: encoded };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to URL encode' };
  }
}

// ============================================================================
// Base64 Operations
// ============================================================================

function base64Decode(input: string): DecoderResult {
  try {
    const normalized = normalizeBase64Input(input);
    if (!normalized) {
      throw new Error('Invalid Base64 string');
    }

    const decoded = decodeNormalizedBase64(normalized);
    return { success: true, output: decoded };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: 'Invalid Base64 string',
    };
  }
}

function base64Encode(input: string): DecoderResult {
  try {
    const encoded = btoa(input);
    return { success: true, output: encoded };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: 'Failed to Base64 encode (contains non-Latin1 characters)',
    };
  }
}

// ============================================================================
// Hex Operations
// ============================================================================

function hexDecode(input: string): DecoderResult {
  try {
    // Remove common prefixes and whitespace
    let cleaned = input.replace(/^(0x|\\x)/i, '').replace(/\s/g, '');

    // Validate hex string
    if (!/^[0-9a-f]+$/i.test(cleaned)) {
      throw new Error('Invalid hexadecimal string');
    }

    // Ensure even length
    if (cleaned.length % 2 !== 0) {
      cleaned = '0' + cleaned;
    }

    let decoded = '';
    for (let i = 0; i < cleaned.length; i += 2) {
      const byte = cleaned.substring(i, i + 2);
      decoded += String.fromCharCode(Number.parseInt(byte, 16));
    }

    return { success: true, output: decoded };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: _error instanceof Error ? _error.message : 'Failed to hex decode',
    };
  }
}

function hexEncode(input: string): DecoderResult {
  try {
    let encoded = '';
    for (let i = 0; i < input.length; i++) {
      const hex = input.charCodeAt(i).toString(16).padStart(2, '0');
      encoded += hex;
    }
    return { success: true, output: encoded };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to hex encode' };
  }
}

// ============================================================================
// HTML Operations
// ============================================================================

function htmlDecode(input: string): DecoderResult {
  try {
    return { success: true, output: decode(input) };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to HTML decode' };
  }
}

function htmlEncode(input: string): DecoderResult {
  try {
    return { success: true, output: encode(input) };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to HTML encode' };
  }
}

// ============================================================================
// Unicode Operations
// ============================================================================

function unicodeDecode(input: string): DecoderResult {
  try {
    const decoded = input.replace(/\\u([0-9a-f]{4})/gi, (match, grp) => {
      return String.fromCharCode(Number.parseInt(grp, 16));
    });
    return { success: true, output: decoded };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to Unicode decode' };
  }
}

function unicodeEncode(input: string): DecoderResult {
  try {
    let encoded = '';
    for (let i = 0; i < input.length; i++) {
      const code = input.charCodeAt(i);
      if (code > 127) {
        encoded += '\\u' + code.toString(16).padStart(4, '0');
      } else {
        encoded += input[i];
      }
    }
    return { success: true, output: encoded };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to Unicode encode' };
  }
}

// ============================================================================
// JWT Operations
// ============================================================================

function jwtDecode(input: string): DecoderResult {
  try {
    const parts = input.trim().split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format (must have 3 parts)');
    }
    const header = decodeProtectedHeader(input);
    const payload = decodeJwt(input);

    const result = {
      header,
      payload,
      signature: parts[2],
    };

    return {
      success: true,
      output: JSON.stringify(result, null, 2),
    };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: _error instanceof Error ? _error.message : 'Invalid JWT',
    };
  }
}

// ============================================================================
// JSON Operations
// ============================================================================

function jsonPretty(input: string): DecoderResult {
  try {
    const parsed = JSON.parse(input);
    return { success: true, output: JSON.stringify(parsed, null, 2) };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: 'Invalid JSON',
    };
  }
}

function jsonMinify(input: string): DecoderResult {
  try {
    const parsed = JSON.parse(input);
    return { success: true, output: JSON.stringify(parsed) };
  } catch (_error) {
    return {
      success: false,
      output: '',
      error: 'Invalid JSON',
    };
  }
}

// ============================================================================
// Hash Operations
// ============================================================================

async function hashText({
  input,
  algorithm,
}: {
  input: string;
  algorithm: 'SHA-1' | 'SHA-256' | 'SHA-512';
}): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest(algorithm, data);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hashByAlgorithm({
  input,
  algorithm,
}: {
  input: string;
  algorithm: 'SHA-1' | 'SHA-256' | 'SHA-512';
}): Promise<DecoderResult> {
  try {
    const hash = await hashText({ input, algorithm });
    return { success: true, output: hash };
  } catch (_error) {
    return { success: false, output: '', error: 'Failed to hash' };
  }
}

// ============================================================================
// Smart Decode
// ============================================================================

async function smartDecode(input: string): Promise<DecoderResult> {
  const detections = detectEncodings(input);

  if (detections.length === 0) {
    return {
      success: false,
      output: input,
      error: 'No encoding detected',
    };
  }

  // Try decoding with the highest confidence detection
  const bestDetection = detections[0];
  return executeOperation({ type: bestDetection.operation, input });
}

export function detectEncodings(input: string): SmartDecodeDetection[] {
  const detections: SmartDecodeDetection[] = [];
  const trimmedInput = input.trim();

  // JWT Detection
  if (
    /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/.test(trimmedInput)
  ) {
    detections.push({
      operation: 'jwt-decode',
      confidence: 0.95,
      reason: 'Matches JWT pattern (eyJ...)',
    });
  }

  // Base64 Detection
  const normalizedBase64 = normalizeBase64Input(trimmedInput);
  const isBase64Shaped = normalizedBase64 !== null && trimmedInput.length >= 8;
  if (isBase64Shaped) {
    const decodedBase64 = decodeNormalizedBase64(normalizedBase64);
    const hasStrongBase64Indicators =
      /[+/]/.test(trimmedInput) || /=+$/.test(trimmedInput);
    const hasReadableDecodedContent =
      decodedBase64 !== null && isMostlyReadableText(decodedBase64);
    if (
      decodedBase64 !== null &&
      (hasStrongBase64Indicators || hasReadableDecodedContent)
    ) {
      detections.push({
        operation: 'base64-decode',
        confidence: hasStrongBase64Indicators ? 0.85 : 0.6,
        reason: hasStrongBase64Indicators
          ? 'Matches Base64 pattern with padding/special chars'
          : 'Possible Base64 (alphanumeric, readable decode)',
      });
    }
  }

  // URL Encoding Detection
  if (/%[0-9A-Fa-f]{2}/.test(input)) {
    const percentCount = (input.match(/%[0-9A-Fa-f]{2}/g) || []).length;
    const confidence = Math.min(0.9, 0.5 + percentCount * 0.05);
    detections.push({
      operation: 'url-decode',
      confidence,
      reason: `Contains ${percentCount} URL-encoded sequences`,
    });
  }

  // HTML Entities Detection
  if (/&[a-z]+;|&#\d+;|&#x[0-9a-f]+;/i.test(input)) {
    const entityCount = (input.match(/&[a-z]+;|&#\d+;|&#x[0-9a-f]+;/gi) || [])
      .length;
    detections.push({
      operation: 'html-decode',
      confidence: 0.85,
      reason: `Contains ${entityCount} HTML entities`,
    });
  }

  // Hex Detection
  if (
    /^(0x)?[0-9a-f]+$/i.test(trimmedInput) &&
    trimmedInput.length % 2 === 0 &&
    trimmedInput.length > 10
  ) {
    const has0xPrefix = /^0x/i.test(trimmedInput);
    detections.push({
      operation: 'hex-decode',
      confidence: has0xPrefix ? 0.85 : 0.7,
      reason: has0xPrefix
        ? 'Hex string with 0x prefix'
        : 'Matches hexadecimal pattern',
    });
  }

  // Unicode Escape Detection
  if (/\\u[0-9a-f]{4}/i.test(input)) {
    detections.push({
      operation: 'unicode-decode',
      confidence: 0.9,
      reason: 'Contains Unicode escape sequences',
    });
  }

  // Sort by confidence (descending)
  return detections.sort((a, b) => b.confidence - a.confidence);
}

function normalizeBase64Input(input: string): string | null {
  const cleaned = input.replace(/\s/g, '');
  if (!/^[A-Za-z0-9+/_-]+={0,2}$/.test(cleaned)) {
    return null;
  }

  const unpadded = cleaned.replace(/=+$/, '');
  const paddingLength = cleaned.length - unpadded.length;
  if (paddingLength > 2 || unpadded.length % 4 === 1) {
    return null;
  }

  const base64 = unpadded.replace(/-/g, '+').replace(/_/g, '/');
  return base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
}

function decodeNormalizedBase64(input: string): string {
  const binary = atob(input);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function isMostlyReadableText(input: string): boolean {
  if (input.length === 0) {
    return false;
  }

  const printableCount = Array.from(input).filter((character) =>
    /[\t\n\r -~]/.test(character),
  ).length;
  return printableCount / input.length >= 0.85;
}
